import React from "react";
import type { ApolloClient } from "@apollo/client";
import { toast } from "react-toastify";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { Card, Col, Row } from "react-bootstrap";
import { createErrorSelector } from "store/selectors";
import {
  clearOAuthPlaidLinkError,
  linkBank,
  verifyBankAccount,
} from "actions/bankActions";
import { bankConstants } from "actions/types";
import { progressiveOnboardingEvents } from "statics/states";
import { updateProgressiveOnboarding } from "actions/employerActions";
import { getLimitByType, hasAddedBank } from "store/selectors/employer";
import { FiAlertTriangle } from "react-icons/fi";
import { isEmployerSelector } from "store/selectors/user";
import {
  activeBanksSelector,
  banksSelector,
  pendingBanksSelector,
} from "store/selectors/bank";
import type { RootState } from "types/state";

import Button from "components/Button";
import Alert from "components/Alert";
import ReAuthPlaidLink from "components/ReAuthPlaidLink";
import PlaidLinkUpdateCard from "components/PlaidLinkUpdateCard";
import AddBankCard from "pages/dashboards/AddBankCard";
import BankCard from "pages/dashboards/individualDashboard/contributions/overview/BankCard";
import RemoveBank from "./RemoveBank";
import EmployerWalletCard from "./EmployerWalletCard";

const actions = [
  bankConstants.LINK_BANK_WITH_ICON,
  bankConstants.OAUTH_PLAID_LINK_TOKEN,
];

const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    bankAccounts: banksSelector(state),
    activeBanks: activeBanksSelector(state),
    pendingBanks: pendingBanksSelector(state),
    error: errorSelector(state),
    hasAddedBank: hasAddedBank(state),
    // getLimitByType returns the raw limit value, which may be a STRING
    // (e.g. "5"); the JSX comparison relies on JS numeric coercion.
    bankAccountLimit: getLimitByType(state, "EmployerBankSizeLimit") as
      | string
      | number,
    isEmployer: isEmployerSelector(state),
  };
};

const mapDispatchToProps = {
  clearOAuthPlaidLinkError,
  linkBank,
  verifyBankAccount,
  updateProgressiveOnboarding,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  updateExpiringLink?: boolean;
  onSuccess?: (token: string, metadata: unknown, userLegalName: string) => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  isDeleting: boolean;
}

class BankAccounts extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { isDeleting: false };
  }

  componentWillUnmount() {
    this.props.clearOAuthPlaidLinkError();
  }

  _handleOnSuccess = (
    token: string,
    metadata: unknown,
    userLegalName: string
  ) => {
    if (this.props.onSuccess) {
      this.props.onSuccess(token, metadata, userLegalName);
      toast.success("Bank account successfully re-authenticated.");
    }
  };

  _getPlaidLinkUpdateCard = (isExpiredBank: string | null) => {
    const bank = this.props.activeBanks.find(
      (bank) => bank.id === isExpiredBank
    );

    return (
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div style={{ marginRight: "10px" }}>
          <Button
            size="sm"
            color="red"
            name="action"
            btnLabel="Remove Bank"
            onClick={() => this.setState({ isDeleting: true })}
          />
        </div>
        <div>
          <ReAuthPlaidLink
            bank={bank}
            // @ts-expect-error isEmployer is a redux-mapped prop on the
            // connected ReAuthPlaidLink (not an external own prop); the
            // original JS passes it through inertly — preserve as-is.
            isEmployer={this.props.isEmployer}
            onSuccess={this._handleOnSuccess}
          />
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className="download-employee-template">
        <div className="main-content">
          <Row>
            <Col md={12}>
              <Row>
                <Col md={12}>
                  <Card>
                    <Card.Title>
                      <p className="page-header">Linked Accounts</p>
                      <p className="subtext">
                        Securely connect your business bank account to Icon in
                        order to facilitate your employee{"'"}s payroll
                        deductions.
                      </p>
                    </Card.Title>
                    <Card.Body>
                      {this.props.error && (
                        <Alert type="error" msg={this.props.error} />
                      )}

                      <Row id="add-banks">
                        {this.props.activeBanks.map((b) => {
                          const needsReAuth = !!b.loginRequired;

                          const isExpiredBank = needsReAuth ? b.id : null;
                          const bankAccountName = b.bankName;
                          const lastFourDigits = b.accountId;
                          const description = `Re-authenticate your ${bankAccountName} bank account ending in ${lastFourDigits} to resume payroll deductions.`;
                          return (
                            <Col key={b.id} md={6}>
                              {needsReAuth ? (
                                <>
                                  {this.state.isDeleting ? (
                                    <RemoveBank
                                      bankId={isExpiredBank}
                                      onCancel={() =>
                                        this.setState({ isDeleting: false })
                                      }
                                    />
                                  ) : (
                                    <PlaidLinkUpdateCard
                                      key={b.id}
                                      icon={
                                        <FiAlertTriangle
                                          color="white"
                                          stroke="#B12121"
                                          size={16}
                                        />
                                      }
                                      title="Bank Re-Authentication Required!"
                                      description={description}
                                      color="red"
                                      // @ts-expect-error withArrow is not a
                                      // PlaidLinkUpdateCard prop; the original
                                      // JS passes it inertly — preserve as-is.
                                      withArrow={true}
                                      onClick={this._getPlaidLinkUpdateCard(
                                        isExpiredBank
                                      )}
                                    />
                                  )}
                                </>
                              ) : (
                                <BankCard
                                  allowContribution={false}
                                  key={b.id}
                                  bankAccount={b}
                                />
                              )}
                            </Col>
                          );
                        })}
                        {this.props.pendingBanks.map((b) => {
                          return (
                            <Col key={b.id} md={6}>
                              <AddBankCard key={b.id} bankAccount={b} />
                            </Col>
                          );
                        })}
                        {/* bankAccountLimit may be a string at runtime (see
                            mapStateToProps); the original JSX relies on JS
                            numeric coercion in this `<` comparison. The cast is
                            type-only and emits no runtime code — preserve the
                            coercion behavior exactly. */}
                        {this.props.bankAccounts.length <
                          (this.props.bankAccountLimit as number) && (
                          <Col md={6}>
                            <AddBankCard
                              insertOnlyMode
                              onSuccess={() => {
                                if (!this.props.hasAddedBank) {
                                  // @ts-expect-error pre-existing bug: the
                                  // action creator expects a 3rd `companyId`
                                  // arg, but the original JS calls it with 2.
                                  // Preserve the original call shape exactly.
                                  void this.props.updateProgressiveOnboarding(
                                    this.props.client as ApolloClient<object>,
                                    progressiveOnboardingEvents.AddBank
                                  );
                                }
                              }}
                            />
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <EmployerWalletCard />
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(BankAccounts));
